import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Building2, ClipboardList, FileSignature, Percent } from 'lucide-react';
import {
  REZIMY_DPH,
  SMLOUVA_TYPY,
  VYCHOZI_TRANSE_POZASTAVKY,
  VYCHOZI_ZARUKA_MESICU,
  type RezimDph,
  type SmlouvaTyp,
} from '@helfr/domain';
import { api, hlaskaZChyby } from '../api/client.js';
import type { Smlouva, Zakazka } from '../api/types.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';
import { REZIM_DPH_LABEL, SMLOUVA_TYP_LABEL } from '../lib/labels.js';
import { TranseEditor, soucetProcent, type TranseRadek } from '../components/TranseEditor.js';
import { Btn, Chyba, Input, Napoveda, Pole, PoleKarta, Sekce, Select } from '../components/ui.js';

/** Výchozí šablona 5/2/2/1 z @helfr/domain — předvyplněná, editovatelná per smlouva. */
const vychoziTranse = (): TranseRadek[] =>
  VYCHOZI_TRANSE_POZASTAVKY.map((t) => ({
    procento: String(t.procento),
    spoustec: t.spoustec,
    odkladMesicu: t.odkladMesicu,
  }));

/**
 * Založení smlouvy — formulář s hierarchií (pravidla IxDF, blueprint 16):
 * hero karta „S kým a za kolik" (typ + protistrana + cena), náležitosti
 * v druhé kartě, pozastávka sbalená (výchozí šablona většině vyhovuje).
 */
export function SmlouvaNovaPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { data: zakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });

  const [zakazkaId, setZakazkaId] = useState(searchParams.get('zakazka') ?? '');
  // Předvyplnit zakázku z globálního přepínače (deep-link ?zakazka má přednost).
  const { vybranaId } = useVybranaZakazka(zakazky);
  useEffect(() => {
    if (!zakazkaId && vybranaId) setZakazkaId(vybranaId);
  }, [vybranaId, zakazkaId]);
  const vybranaZakazka = zakazky?.find((z) => z.id === zakazkaId);
  const [typ, setTyp] = useState<SmlouvaTyp>('hlavni');
  const [cislo, setCislo] = useState('');
  const [protistrana, setProtistrana] = useState('');
  const [cena, setCena] = useState('');
  const [zarukaMesicu, setZarukaMesicu] = useState(String(VYCHOZI_ZARUKA_MESICU));
  const [verejnaZakazka, setVerejnaZakazka] = useState(false);
  const [rezimDph, setRezimDph] = useState<RezimDph>('prenesena_danova_povinnost');
  const [datumPodpisu, setDatumPodpisu] = useState('');
  const [platebniPodminky, setPlatebniPodminky] = useState('');
  const [transe, setTranse] = useState<TranseRadek[]>(vychoziTranse);

  const zalozit = useMutation({
    mutationFn: () =>
      api.post<Smlouva>('/api/contracts', {
        zakazkaId,
        typ,
        cislo: cislo || null,
        protistrana,
        cena: cena.replace(',', '.').replace(/[\s\u00a0]/g, ''),
        zarukaMesicu: zarukaMesicu === '' ? null : Number(zarukaMesicu),
        verejnaZakazka,
        rezimDph,
        datumPodpisu: datumPodpisu || null,
        platebniPodminky: platebniPodminky || null,
        transe: transe.map((t) => ({
          procento: t.procento.replace(',', '.').trim(),
          spoustec: t.spoustec,
          odkladMesicu: t.odkladMesicu,
        })),
      }),
    onSuccess: (smlouva) => void navigate(`/smlouvy/${smlouva.id}`),
  });

  const odeslat = (e: FormEvent) => {
    e.preventDefault();
    zalozit.mutate();
  };

  return (
    <>
      <div>
        <div className="text-sm text-text-secondary">
          <Link to="/smlouvy" className="underline underline-offset-2">
            Smlouvy
          </Link>{' '}
          / nová
        </div>
        <h1 className="text-2xl font-bold">Nová smlouva</h1>
        <p className="text-base text-text-secondary">
          {vybranaZakazka
            ? `Pro zakázku ${vybranaZakazka.cislo} — ${vybranaZakazka.nazev} (přepíná se nahoře).`
            : 'Základ cesty peněz — na smlouvu se zavěsí ceník, změny i dodatky.'}
        </p>
      </div>

      <form onSubmit={odeslat} className="flex max-w-3xl flex-col gap-4">
        {/* HERO: to podstatné — strany a cena. */}
        <PoleKarta
          ikona={<FileSignature size={16} strokeWidth={1.75} />}
          nadpis="Základ smlouvy"
          vaha="hlavni"
        >
          <div className="flex flex-col gap-4">
            {/* Typ = dvě velké volby s viditelným radio kolečkem — musí být
                na první pohled jasné, že se vybírá jedna z možností. */}
            <div>
              <span className="mb-1 block text-sm font-semibold text-text-secondary">
                Typ smlouvy<span className="text-error-text"> *</span>
              </span>
              <div role="radiogroup" aria-label="Typ smlouvy" className="grid gap-2 sm:grid-cols-2">
                {SMLOUVA_TYPY.map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={typ === t}
                    onClick={() => setTyp(t)}
                    className={`flex min-h-14 items-center gap-3 rounded-lg border px-3 py-2 text-left transition-colors duration-100 ${
                      typ === t
                        ? 'border-primary bg-primary-tint'
                        : 'border-border bg-surface hover:border-border-strong'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
                        typ === t ? 'border-primary' : 'border-border-strong'
                      }`}
                    >
                      {typ === t && <span className="size-2.5 rounded-full bg-primary" />}
                    </span>
                    <span className="min-w-0">
                      <span
                        className={`block text-base ${typ === t ? 'font-bold text-primary' : 'font-semibold'}`}
                      >
                        {SMLOUVA_TYP_LABEL[t]}
                      </span>
                      <span className="block text-sm font-normal text-text-secondary">
                        {t === 'hlavni' ? 'my dodáváme investorovi' : 'subdodavatel dodává nám'}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <Pole popisek="Protistrana" povinne>
              <Input
                required
                placeholder={typ === 'hlavni' ? 'Develop Invest a.s.' : 'ELMONT elektro s.r.o.'}
                value={protistrana}
                onChange={(e) => setProtistrana(e.target.value)}
              />
            </Pole>

            {/* Cena = hero číslo smlouvy. */}
            <Pole popisek="Cena Díla" povinne napoveda="netto, bez DPH">
              <div className="relative sm:max-w-sm">
                <Input
                  required
                  inputMode="decimal"
                  placeholder="42 000 000"
                  className="pr-24 text-2xl! font-bold tabular-nums"
                  value={cena}
                  onChange={(e) => setCena(e.target.value)}
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-base text-text-secondary">
                  Kč bez DPH
                </span>
              </div>
            </Pole>
          </div>
        </PoleKarta>

        {/* Náležitosti — důležité, ale ne hero. */}
        <PoleKarta
          ikona={<ClipboardList size={16} strokeWidth={1.75} />}
          nadpis="Náležitosti smlouvy"
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Pole popisek="Číslo smlouvy">
              <Input
                placeholder="SOD-2026-001"
                value={cislo}
                onChange={(e) => setCislo(e.target.value)}
              />
            </Pole>
            <Pole popisek="Datum podpisu">
              <Input
                type="date"
                value={datumPodpisu}
                onChange={(e) => setDatumPodpisu(e.target.value)}
              />
            </Pole>
            <Pole popisek="Záruka (měsíce)">
              <Input
                type="number"
                min={0}
                value={zarukaMesicu}
                onChange={(e) => setZarukaMesicu(e.target.value)}
              />
            </Pole>
            <Pole popisek="Režim DPH">
              <Select value={rezimDph} onChange={(e) => setRezimDph(e.target.value as RezimDph)}>
                {REZIMY_DPH.map((r) => (
                  <option key={r} value={r}>
                    {REZIM_DPH_LABEL[r]}
                  </option>
                ))}
              </Select>
            </Pole>
            <Pole popisek="Platební podmínky">
              <Input
                placeholder="Měsíční fakturace, splatnost 45 dní"
                value={platebniPodminky}
                onChange={(e) => setPlatebniPodminky(e.target.value)}
              />
            </Pole>
            <div className="flex items-center gap-2 self-end">
              <label className="flex min-h-11 items-center gap-3">
                <input
                  type="checkbox"
                  className="size-6"
                  checked={verejnaZakazka}
                  onChange={(e) => setVerejnaZakazka(e.target.checked)}
                />
                <span className="font-semibold">Veřejná zakázka</span>
              </label>
              {/* Paragrafové znění režimu za „?" (blueprint 16 — žargon pryč z plátna). */}
              <Napoveda titulek="Co znamená veřejná zakázka">
                <p>
                  U veřejné zakázky platí zákonné limity změn (režim § 222 ZZVZ). Zaškrtnutí zapne
                  hlídání těchto limitů v přehledu Vícepráce.
                </p>
              </Napoveda>
            </div>
          </div>
        </PoleKarta>

        {/* Pozastávka — výchozí šablona většině SOD vyhovuje → sbaleno. */}
        <Sekce
          titulek="Pozastávka — tranše"
          podtitulek={`šablona ${transe.map((t) => t.procento || '?').join('/')} · Σ ${soucetProcent(transe)} %`}
        >
          <div className="flex flex-col gap-3">
            <p className="flex items-center gap-2 text-sm text-text-secondary">
              <Percent size={14} strokeWidth={2} aria-hidden="true" className="shrink-0" />
              Předvyplněna výchozí šablona 5/2/2/1 — upravte podle SOD. Základ = cena Díla vč.
              dodatků.
            </p>
            <TranseEditor transe={transe} onChange={setTranse} />
          </div>
        </Sekce>

        {zalozit.error ? <Chyba>{hlaskaZChyby(zalozit.error)}</Chyba> : null}

        {/* Jedna primární akce; návrat je tichý odkaz (pravidlo wizardů). */}
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            className="min-h-11 rounded-md px-2 text-sm font-semibold text-text-secondary hover:text-text-primary"
            onClick={() => void navigate(-1)}
          >
            Zrušit
          </button>
          <Btn type="submit" disabled={zalozit.isPending}>
            <Building2 size={16} strokeWidth={2} aria-hidden="true" />
            {zalozit.isPending ? 'Zakládám…' : 'Založit smlouvu'}
          </Btn>
        </div>
      </form>
    </>
  );
}
